import { useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import cx from 'classnames';

import chevronRightSvg from 'assets/icons/chevron_right.svg';
import { DropdownItemProps } from 'components/common/types/DropdownItem.types';
import { INDETERMINATE } from 'lib/Checkbox';
import { getItemSelectionStatus, getLeafNodes, ITEM_SELECTION_STATUS, Option } from 'utils/multiDimensionalListUtils';
import { DropdownItemContainer } from './DropdownItemContainer';
import { ExpandableDropdownOption } from './ExpandableDropdownOption';
import { SingleDropdownOption } from './SingleDropdownOption';

const toCheckboxState = (status: string): boolean | typeof INDETERMINATE => {
  if (status === ITEM_SELECTION_STATUS.CHECKED) return true;
  if (status === ITEM_SELECTION_STATUS.INDETERMINATE) return INDETERMINATE;
  return false;
};

export const DropdownItem = ({
  multiLevelDropdownId,
  option,
  isSubMenuOpen = false,
  setIsSubMenuOpen,
  isSelectable = false,
  selectedItems = [],
  onChangeSelectedItem,
  isPositionFixed = false,
  depth = 0,
}: DropdownItemProps): React.ReactElement => {
  const [openSubMenu, setOpenSubMenu] = useState<number | null>(null);

  useEffect(() => {
    if (!isSubMenuOpen) setOpenSubMenu(null);
  }, [isSubMenuOpen]);

  const onChange = (checked: boolean): void =>
    getLeafNodes(option).forEach((element: Option) => onChangeSelectedItem?.(element, checked));

  const itemSelectionStatus = getItemSelectionStatus(option, selectedItems);
  const checkboxIsSelected = toCheckboxState(itemSelectionStatus);
  const itemLabel = `${multiLevelDropdownId}-${option.name}-item-${depth}`;

  return option.children?.length ? (
    <>
      <li
        aria-label={itemLabel}
        key={itemLabel}
        className={cx('cursor-pointer px-3', { relative: depth > 0 })}
        onMouseEnter={() => setIsSubMenuOpen?.(option.code)}
      >
        <div
          className={cx(
            'flex p-2 mb-0.5 text-neutral-900 body-base rounded-md justify-between items-center hover:bg-neutral-200 hover:text-primary-950',
            {
              'bg-neutral-200 text-primary-950 hovered-dropdown-item': isSubMenuOpen,
            },
          )}
        >
          <ExpandableDropdownOption
            option={option}
            isSelectable={isSelectable}
            checkboxIsSelected={checkboxIsSelected}
            itemSelectionStatus={itemSelectionStatus}
            onChange={onChange}
          />
          <SVG src={chevronRightSvg} className="h-4 w-4 text-neutral-950-opacity-50" />
        </div>
      </li>

      {isSubMenuOpen && (
        <DropdownItemContainer dropdownId={multiLevelDropdownId} optionName={option.name} depth={depth}>
          {option.children.map((opt) => (
            <DropdownItem
              multiLevelDropdownId={multiLevelDropdownId}
              option={opt}
              key={opt.code}
              setIsSubMenuOpen={(code: number) => {
                setOpenSubMenu(code);
              }}
              isSubMenuOpen={openSubMenu === opt.code}
              isSelectable={opt.selectable}
              selectedItems={selectedItems}
              onChangeSelectedItem={onChangeSelectedItem}
              depth={depth + 1}
              isPositionFixed={isPositionFixed}
            />
          ))}
        </DropdownItemContainer>
      )}
    </>
  ) : (
    <li aria-label={itemLabel} key={itemLabel} className="cursor-pointer px-3">
      <div className="flex p-2 mb-0.5 text-neutral-900 body-base rounded-md items-center hover:bg-neutral-200 hover:text-primary-950">
        <SingleDropdownOption
          option={option}
          isSelectable={isSelectable}
          checkboxIsSelected={checkboxIsSelected}
          itemSelectionStatus={itemSelectionStatus}
          onChange={onChange}
        />
      </div>
    </li>
  );
};

export default DropdownItem;
